// KPIBasket.tsx
import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Styles from "../BusinessDescriptionForm.module.css";

export type KPIBasketData = {
  indicater_name: any;
  what_does_it_measure: any;
  unit_of_measure: any;
  what_is_the_calculation_formula: any;
  Check_Items: any;
  who_is_responsible: any;
  Is_it_related_to_any_global_goal: any;
  Which_goal: any;
  year: any;
  current_YDT_value: any;
  previous_FY_year: any;
  target_value: any;
};

interface KPIBasketProps {
  setKpiBasketData: React.Dispatch<React.SetStateAction<KPIBasketData[]>>;
  kpiBasketData: KPIBasketData[];
  isKpiBasketFilled: boolean;
  accordions: KPIBasketData[];
  setAccordions: React.Dispatch<React.SetStateAction<KPIBasketData[]>>;
}

const KPIBasket: React.FC<KPIBasketProps> = ({
  setKpiBasketData,
  kpiBasketData,
  isKpiBasketFilled,
  accordions,
  setAccordions,
}) => {
  // Use the first item of kpiBasketData as the current form data
  const jobHierachyData = kpiBasketData.length > 0 ? kpiBasketData[0] : {
    indicater_name: [""],
    what_does_it_measure: [""],
    unit_of_measure: [""],
    what_is_the_calculation_formula: [""],
    Check_Items: [""],
    who_is_responsible: [""],
    Is_it_related_to_any_global_goal: [""],
    Which_goal: [""],
    year: [""],
    current_YDT_value: [""],
    previous_FY_year: [""],
    target_value: [""],
  };

  const jobHierachyPlaceholders: { [key in keyof KPIBasketData]: string } = {
    indicater_name: "Insert the full name of the indicator",
    what_does_it_measure:
      "Identify which variable is being analyzed to which process it relates",
    unit_of_measure: "Describe how the measurement is performed",
    what_is_the_calculation_formula: "Insert the standard calculation formula",
    Check_Items:
      "Mention the benefits that monitoring the indicator provides / What problems it prevents from happening",
    who_is_responsible: "Enter Resources",
    Is_it_related_to_any_global_goal: "Yes / No",
    Which_goal: "Enter Objectives",
    year: "Select Year",
    current_YDT_value: "Enter numeric value only",
    previous_FY_year: "Enter numeric value only",
    target_value: "Enter numeric value only",
  };

  const handleJobHierarchyInputChange = (
    category: keyof KPIBasketData,
    index: number,
    value: string | number
  ) => {
    // For numeric fields, validate the input
    if (["current_YDT_value", "previous_FY_year", "target_value"].includes(category)) {
      // Allow empty string or numeric values
      if (value !== "" && isNaN(Number(value))) {
        return; // Don't update state if not a number
      }
    }

    const updated = {
      ...jobHierachyData,
      [category]: jobHierachyData[category].map((item: any, i: number) =>
        i === index ? value : item
      ),
    };

    // Update the parent state
    setKpiBasketData([updated]);
  };

  const addQuestionMark = (label: string) => {
    if (/^(What|Is|Who|Which|How|When)/i.test(label)) {
      return `${label}?`;
    }
    return label;
  };

  const handleAddAccordion = () => {
    if (isKpiBasketFilled) {
      const updatedKpiData = [...accordions, jobHierachyData];
      setAccordions(updatedKpiData);

      // Also update the kpiBasketData to include all accordions plus the current form
      setKpiBasketData([{
        indicater_name: [""],
        what_does_it_measure: [""],
        unit_of_measure: [""],
        what_is_the_calculation_formula: [""],
        Check_Items: [""],
        who_is_responsible: [""],
        Is_it_related_to_any_global_goal: [""],
        Which_goal: [""],
        year: [""],
        current_YDT_value: [""],
        previous_FY_year: [""],
        target_value: [""],
      }]);
    }
  };

  const handleRemoveAccordion = (index: number) => {
    // Remove the accordion at the specified index
    const updatedAccordions = accordions.filter((_, i) => i !== index);
    setAccordions(updatedAccordions);
    
    // Also update the kpiBasketData to reflect the change
    setKpiBasketData(prev => {
      // If we're removing the only item, reset to empty form
      if (updatedAccordions.length === 0) {
        return [{
          indicater_name: [""],
          what_does_it_measure: [""],
          unit_of_measure: [""],
          what_is_the_calculation_formula: [""],
          Check_Items: [""],
          who_is_responsible: [""],
          Is_it_related_to_any_global_goal: [""],
          Which_goal: [""],
          year: [""],
          current_YDT_value: [""],
          previous_FY_year: [""],
          target_value: [""],
        }];
      }
      // Otherwise keep the remaining accordions
      return [...updatedAccordions];
    });
  };

  // Add this effect to handle the "Which goal?" field state
  useEffect(() => {
    const currentGoalRelation = jobHierachyData.Is_it_related_to_any_global_goal[0];
    if (currentGoalRelation === "No") {
      // Reset "Which goal?" field when "No" is selected
      handleJobHierarchyInputChange("Which_goal", 0, "");
    }
  }, [jobHierachyData.Is_it_related_to_any_global_goal[0]]);

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ color: "#000", pt: "20px" }}>
        KPI Basket
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "48% 48%",
          rowGap: 2,
          columnGap: 4,
          borderRadius: "10px",
          border: "1px solid #989898",
          boxShadow: "1px 1px 19.3px 0px rgba(0, 0, 0, 0.02)",
          padding: "40px 30px",
        }}
      >
        {Object.entries(jobHierachyData).map(([category, fields]) => (
          <Box key={category}>
            <Typography variant="subtitle1" gutterBottom>
              {addQuestionMark(
                category.charAt(0).toUpperCase() +
                category.slice(1).replace(/_/g, " ")
              )}
            </Typography>
            {fields.map((field: any, index: number) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  color: "#000",
                }}
              >
                {category === "year" ? (
                  <select
                    className={Styles.inputFeild}
                    value={field}
                    required
                    onChange={(e) =>
                      handleJobHierarchyInputChange(
                        category as keyof KPIBasketData,
                        index,
                        e.target.value
                      )
                    }
                  >
                    <option value="" disabled>
                      Select Year
                    </option>
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                ) : category === "Is_it_related_to_any_global_goal" ? (
                  <select
                    className={Styles.inputFeild}
                    value={field}
                    required
                    onChange={(e) =>
                      handleJobHierarchyInputChange(
                        category as keyof KPIBasketData,
                        index,
                        e.target.value
                      )
                    }
                  >
                    <option value="" disabled>
                      Select Yes or No
                    </option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : (
                  <input
                    className={Styles.inputFeild}
                    value={field}
                    required
                    onChange={(e) =>
                      handleJobHierarchyInputChange(
                        category as keyof KPIBasketData,
                        index,
                        e.target.value
                      )
                    }
                    placeholder={
                      jobHierachyPlaceholders[category as keyof KPIBasketData]
                    }
                    type={["current_YDT_value", "previous_FY_year", "target_value"].includes(category) ? "number" : "text"}
                    disabled={
                      category === "Which_goal" &&
                      jobHierachyData.Is_it_related_to_any_global_goal[index] ===
                      "No"
                    }
                    onKeyDown={(e) => {
                      // Prevent typing 'e', '+', '-', '.' for numeric fields
                      if (["current_YDT_value", "previous_FY_year", "target_value"].includes(category)) {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Button
        type="submit"
        onClick={handleAddAccordion}
        disabled={!isKpiBasketFilled}
        sx={{
          float: "right",
          textTransform: "capitalize",
          color: "#000",
          textDecoration: "underline",
          fontWeight: "bold",
        }}
      >
        Add
      </Button>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography sx={{ margin: "20px 0px" }}>Added KPI Baskets</Typography>
        {accordions.map((accordionData, index) => (
          <Accordion className={Styles.BasketBox} key={index}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id={`panel1-header-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className={Styles.BasketAccTitle}>
                <b>INDICATOR NAME</b>
                <p>{accordionData.indicater_name[0]}</p>
              </div>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // Prevent accordion from expanding/collapsing when clicking the remove button
                  handleRemoveAccordion(index);
                }}
                sx={{
                  color: "red",
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  },
                }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails
              className={Styles.BasketBoxContent}
              sx={{
                display: "grid",
                gridTemplateColumns: "50% 50%",
                gap: 2,
              }}
            >
              <Box className={Styles.BasketAccTitle}>
                <b>INDICATOR NAME</b>
                <p>{accordionData.indicater_name[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>WHAT DOES IT MEASURE?</b>
                <p>{accordionData.what_does_it_measure[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>UNIT OF MEASURE</b>
                <p>{accordionData.unit_of_measure[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>WHAT IS THE CALCULATION FORMULA?</b>
                <p>{accordionData.what_is_the_calculation_formula[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>Check Items</b>
                <p>{accordionData.Check_Items[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>WHO IS RESPONSIBLE?</b>
                <p>{accordionData.who_is_responsible[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>IS IT RELATED TO ANY GLOBAL GOAL?</b>
                <p>{accordionData.Is_it_related_to_any_global_goal[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>WHICH GOAL</b>
                <p>{accordionData.Which_goal[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>YEAR</b>
                <p>{accordionData.year[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>CURRENT YDT VALUE</b>
                <p>{accordionData.current_YDT_value[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>PREVIOUS FY YEAR</b>
                <p>{accordionData.previous_FY_year[0]}</p>
              </Box>
              <Box className={Styles.BasketAccTitle}>
                <b>TARGET VALUE</b>
                <p>{accordionData.target_value[0]}</p>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </>
  );
};

export default KPIBasket;